import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subject, catchError, of, takeUntil } from 'rxjs';
import { AuthService } from '../shared/auth/auth.service';
import { NotificationsApiService } from '../shared/api/notifications-api.service';
import { NotificationDto } from '../shared/api/notifications-api.types';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, RouterOutlet],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.css'
})
export class ShellComponent implements OnInit, OnDestroy {
  sidebarCollapsed = false;

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly notificationsApi: NotificationsApiService
  ) {}

  /** Home / Feed is the only screen that uses the shell search affordance (feed has its own wired search). */
  get showNavbarSearch(): boolean {
    const path = this.router.url.split(/[?#]/)[0];
    return path === '/feed';
  }

  notificationsOpen = false;
  notificationsLoading = false;
  notificationsError = '';
  notifications: NotificationDto[] = [];
  markingAllNotificationsRead = false;

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.authService.refreshCurrentUser().pipe(takeUntil(this.destroy$)).subscribe();
    this.refreshNotifications();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get userDisplayName(): string {
    return this.authService.currentUser?.displayName || 'User';
  }

  /** Short label from token roles (ignores local demo-role UI override). */
  get userRoleLabel(): string {
    const roles = this.authService.backendRoles;
    if (roles.length === 0) {
      return 'Employee';
    }

    const order = ['ADMIN', 'MODERATOR', 'COMMITTEE', 'EMPLOYEE'] as const;
    const set = new Set(roles.map((r) => r.toUpperCase()));
    for (const code of order) {
      if (set.has(code)) {
        const display: Record<string, string> = {
          ADMIN: 'Admin',
          MODERATOR: 'Moderator',
          COMMITTEE: 'Committee',
          EMPLOYEE: 'Employee'
        };
        return display[code] ?? code;
      }
    }
    return roles[0];
  }

  get canAccessCommitteeBoard(): boolean {
    const roles = this.authService.backendRoles;
    return roles.some((r) => {
      const code = r.toUpperCase();
      return code === 'COMMITTEE' || code === 'MODERATOR' || code === 'ADMIN';
    });
  }

  get unreadNotificationsCount(): number {
    return (this.notifications ?? []).filter((n) => !n.isRead).length;
  }

  toggleNotifications(evt: MouseEvent): void {
    evt.stopPropagation();
    this.notificationsOpen = !this.notificationsOpen;
    if (this.notificationsOpen) {
      this.refreshNotifications();
    }
  }

  closeNotifications(): void {
    this.notificationsOpen = false;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.notificationsOpen) {
      this.closeNotifications();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeClose(): void {
    if (this.notificationsOpen) {
      this.closeNotifications();
    }
  }

  refreshNotifications(): void {
    if (this.notificationsLoading) return;
    this.notificationsLoading = true;
    this.notificationsError = '';

    this.notificationsApi
      .get()
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => {
          this.notificationsError = 'Failed to load notifications.';
          return of<NotificationDto[]>([]);
        })
      )
      .subscribe((items) => {
        this.notifications = items ?? [];
        this.notificationsLoading = false;
      });
  }

  markAllNotificationsAsRead(evt?: MouseEvent): void {
    evt?.stopPropagation();
    if (this.markingAllNotificationsRead || this.unreadNotificationsCount === 0) {
      return;
    }

    this.markingAllNotificationsRead = true;
    this.notificationsError = '';

    this.notificationsApi
      .markAllAsRead()
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => {
          this.notificationsError = 'Failed to mark all notifications as read.';
          return of(null);
        })
      )
      .subscribe((result) => {
        if (!result) {
          this.markingAllNotificationsRead = false;
          return;
        }

        const nowIso = new Date().toISOString();
        this.notifications = this.notifications.map((notification) =>
          notification.isRead
            ? notification
            : {
                ...notification,
                isRead: true,
                readAtUtc: notification.readAtUtc ?? nowIso
              }
        );
        this.markingAllNotificationsRead = false;
      });
  }

  openNotification(n: NotificationDto, evt?: MouseEvent): void {
    evt?.preventDefault();
    evt?.stopPropagation();

    const link = (n.link ?? '').trim();
    const afterNav = () => {
      this.closeNotifications();
      if (link) {
        this.router.navigateByUrl(link);
      }
    };

    if (n.isRead) {
      afterNav();
      return;
    }

    this.notificationsApi
      .markAsRead(n.id)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null))
      )
      .subscribe((updated) => {
        if (updated?.id) {
          this.notifications = this.notifications.map((x) => (x.id === updated.id ? updated : x));
        } else {
          this.notifications = this.notifications.map((x) => (x.id === n.id ? { ...x, isRead: true } : x));
        }
        afterNav();
      });
  }

  formatTimeAgo(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return d.toLocaleDateString();
  }

  logout(): void {
    this.authService.logout();
  }
}
